import { TrustiiApiClient } from "../api/client.js";
import { incrementGCounter } from "../crdt/gCounter.js";
import { stampRegister } from "../crdt/lwwRegister.js";
import {
  DeviceKeys,
  devicePublicKeyJwk,
  generateDeviceKeys,
} from "../crypto/keys.js";
import {
  WalletSyncEngine,
  StepUpProvider,
} from "../sync/WalletSyncEngine.js";
import {
  Enrollment,
  EnrollmentBundlePlaintext,
  EnrollmentWireBundle,
  EphemeralHandshake,
} from "./bundle.js";
import { signEnrollmentProof } from "./proof.js";

export interface EnrollmentBundleResult {
  wire: EnrollmentWireBundle;
  deepLink: string;
  proofB64: string;
  bundle: EnrollmentBundlePlaintext;
}

export interface BeginEnrollmentResult {
  deviceKeys: DeviceKeys;
  handshake: EphemeralHandshake;
  qrPayload: string;
}

export interface AcceptEnrollmentResult {
  syncKey: Uint8Array;
  bundle: EnrollmentBundlePlaintext;
}

export interface PrepareEnrollmentRequestInput {
  syncKey: Uint8Array | null;
  deviceKeys: DeviceKeys | null;
  isDeviceRevoked: boolean;
  stepUpAvailable: boolean;
}

export type PrepareEnrollmentResult =
  | { ready: true }
  | { ready: false; reason: string };

export function prepareEnrollmentRequest(
  input: PrepareEnrollmentRequestInput,
): PrepareEnrollmentResult {
  if (!input.syncKey) {
    return { ready: false, reason: "sync_key_missing" };
  }
  if (!input.deviceKeys) {
    return { ready: false, reason: "device_not_initialized" };
  }
  if (input.isDeviceRevoked) {
    return { ready: false, reason: "device_revoked" };
  }
  if (!input.stepUpAvailable) {
    return { ready: false, reason: "step_up_required" };
  }
  return { ready: true };
}

export function prepareEnrollmentForEngine(
  engine: WalletSyncEngine,
  stepUp?: StepUpProvider,
): PrepareEnrollmentResult {
  return prepareEnrollmentRequest({
    syncKey: engine.getSyncKey(),
    deviceKeys: engine.getDeviceKeys(),
    isDeviceRevoked: engine.isLocalDeviceRevoked(),
    stepUpAvailable: Boolean(stepUp ?? engine.getStepUpProvider()),
  });
}

export function beginEnrollment(): BeginEnrollmentResult {
  const deviceKeys = generateDeviceKeys();
  const handshake = Enrollment.createEphemeralHandshake();
  const qrPayload = JSON.stringify({
    type: "trustii-enrollment-handshake",
    ephemeral_public_key_jwk: handshake.ephemeralPublicJwk,
    sync_device_id: deviceKeys.syncDeviceId,
    public_key_jwk: devicePublicKeyJwk(deviceKeys),
  });
  return { deviceKeys, handshake, qrPayload };
}

export function issueEnrollmentBundle(
  handshake: EphemeralHandshake,
  accountId: number,
  syncKey: Uint8Array,
  trustedDeviceKeys: DeviceKeys,
  newDevicePublicJwk: ReturnType<typeof devicePublicKeyJwk>,
  newSyncDeviceId: string,
): EnrollmentBundleResult {
  const { bundle, wire, deepLink } = Enrollment.createBundle(
    trustedDeviceKeys,
    accountId,
    newDevicePublicJwk,
    syncKey,
    newSyncDeviceId,
    handshake.ephemeralPublicJwk,
  );
  const proofB64 = signEnrollmentProof(
    trustedDeviceKeys,
    accountId,
    newSyncDeviceId,
    newDevicePublicJwk,
    bundle.exp,
  );
  return { wire, deepLink, proofB64, bundle };
}

export async function addDeviceToCrdt(
  engine: WalletSyncEngine,
  bundle: EnrollmentBundlePlaintext,
  label = "Enrolled device",
): Promise<void> {
  engine.localMutate((state) => {
    const deviceId = engine.getDeviceKeys()!.syncDeviceId;
    const counter = incrementGCounter(state.clock, deviceId);
    state.devices[bundle.sync_device_id] = {
      device_id: stampRegister(bundle.sync_device_id, counter, deviceId),
      label: stampRegister(label, counter, deviceId),
      public_key_jwk: stampRegister(bundle.public_key_jwk, counter, deviceId),
      signing_public_key: stampRegister(
        Buffer.from(engine.getDeviceKeys()!.signingPublicKey).toString("base64"),
        counter,
        deviceId,
      ),
      wrapped_sync_key: stampRegister(bundle.wrapped_sync_key, counter, deviceId),
      enrolled_at: stampRegister(new Date().toISOString(), counter, deviceId),
    };
  });
  await engine.push();
}

export function acceptEnrollment(
  wireBundle: EnrollmentWireBundle,
  deviceKeys: DeviceKeys,
  ephemeralPrivateKey: Uint8Array,
): AcceptEnrollmentResult {
  const { bundle, syncKey } = Enrollment.consumeBundle(
    wireBundle,
    deviceKeys,
    ephemeralPrivateKey,
  );
  return { syncKey, bundle };
}

export async function registerWithServer(
  api: TrustiiApiClient,
  webauthnId: string,
  bundle: EnrollmentBundlePlaintext,
  proofB64: string,
  stepUpToken: string,
): Promise<{ enrolled: boolean; sync_device_id: string }> {
  return api.enrollSync(webauthnId, {
    sync_device_id: bundle.sync_device_id,
    public_key_jwk: bundle.public_key_jwk as unknown as Record<string, string>,
    enrollment_proof: proofB64,
    step_up_token: stepUpToken,
  });
}

export const WalletEnrollmentCoordinator = {
  prepareEnrollmentRequest,
  prepareEnrollmentForEngine,
  beginEnrollment,
  issueEnrollmentBundle,
  addDeviceToCrdt,
  acceptEnrollment,
  registerWithServer,
};
